import React, { useCallback, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { SoftwareAdminApi, SoftwareStatus } from 'openapi'
import { Skeleton } from 'shadcdn/ui/skeleton'
import { Badge } from 'ui/Badge/Badge'
import { Tabs, TabsList, TabsTrigger } from 'shadcdn/ui/tabs'
import { DebouncedInput } from 'ui/DebouncedInput/DebouncedInput'

import { AdminSoftwareListPath, AdminSofwarePagePath } from '../routes'

import { AdminSoftwareTable } from './components/AdminSoftwareTable'
import { AddSoftwareButton } from './components/AddSoftwareButton'
import { AdminSoftwarePagination } from './components/AdminSoftwarePagination'
import { useSoftwareAdminList } from './hooks'

type Props = {}

export const AdminSoftwareListPage = React.memo((_props: Props) => {
  const { status } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('search') ?? ''
  const ordering = searchParams.get('ordering') ?? ''

  const setOrdering = useCallback(
    (newOrdering: string) => {
      const params: Record<string, string> = {}
      if (search) params.search = search
      if (newOrdering) params.ordering = newOrdering
      setSearchParams(params)
    },
    [search, setSearchParams],
  )

  const PER_PAGE = 50
  const [page, setPage] = useState<number>(0)
  const { data, isFetching } = useSoftwareAdminList({
    limit: PER_PAGE,
    offset: page * PER_PAGE,
    status: status === 'blank-urls' || status === 'pending-versions' ? undefined : (status as SoftwareStatus),
    query: search,
    ordering,
    hasBlankVersionUrls: status === 'blank-urls' ? true : undefined,
    hasPendingVersions: status === 'pending-versions' ? true : undefined,
  })
  const softwareList = data?.items ?? []
  const pagesCount = Math.ceil((data?.count ?? 0) / PER_PAGE) ?? 0

  const navigate = useNavigate()
  const navigateSoftware = useCallback(
    (softwareId: number | 'new') => navigate(AdminSofwarePagePath({ softwareId })),
    [navigate],
  )

  const statusesWithAmountResult = useQuery({
    queryKey: ['SoftwareAdminApi.statuses'],
    queryFn: () => SoftwareAdminApi.statuses(),
    placeholderData: keepPreviousData,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  })

  const statusesWithAmount: Array<[SoftwareStatus | 'blank-urls' | 'pending-versions' | undefined, number]> =
    useMemo(() => {
      const data: Record<string, number> = statusesWithAmountResult.data as any
      if (!data) return []
      const retVal: Array<[SoftwareStatus | 'blank-urls' | 'pending-versions' | undefined, number]> = []

      // Calculate total excluding custom filters
      const totalCount = Object.entries(data)
        .filter(([key]) => key !== 'blank_version_urls' && key !== 'pending_versions')
        .reduce((acc, [_, val]) => acc + val, 0)

      retVal.push([undefined, totalCount])

      // Add blank URLs tab (PENDING with empty URL)
      if (data['blank_version_urls']) {
        retVal.push(['blank-urls', data['blank_version_urls']])
      }

      // Add pending versions tab (PENDING with URL, ready for parsing)
      if (data['pending_versions']) {
        retVal.push(['pending-versions', data['pending_versions']])
      }

      for (const status of Object.values(SoftwareStatus)) {
        if (data[status]) {
          retVal.push([status, data[status]])
        }
      }
      return retVal
    }, [statusesWithAmountResult.data])

  // prettier-ignore
  const filteredSoftware =
    !status || status === 'all' || status === 'blank-urls' || status === 'pending-versions'
      ? softwareList
      : softwareList.filter((software) => software.status === status)

  return (
    <div className="mx-2">
      <div className="flex gap-x-4">
        <div className="flex flex-grow gap-2 mb-1">
          <Tabs defaultValue={status} onValueChange={(val: any) => navigate(AdminSoftwareListPath({ status: val }))}>
            <TabsList className="bg-gray-200">
              {statusesWithAmount.map(([_status, amount]) => (
                <TabsTrigger key={String(_status)} value={_status!}>
                  <span className="capitalize">
                    {_status === 'blank-urls'
                      ? 'Blank URLs'
                      : _status === 'pending-versions'
                        ? 'Pending Versions'
                        : (_status ?? 'All')}
                  </span>
                  <Badge className="ml-2" color={_status === status ? 'gray' : 'white'}>
                    {amount}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <div>
          <AddSoftwareButton navigateSoftware={navigateSoftware} />
        </div>

        <DebouncedInput
          autoFocus={true}
          debounceTime={300}
          value={search}
          onChange={(newSearch = '') => {
            if (newSearch === search) return
            setSearchParams({ search: newSearch })
          }}
          placeholder="Filter..."
          className="py-1 px-2 text-sm mt-1 w-80"
        />
      </div>
      {data && !isFetching ? (
        <>
          <div className="bg-white p-4 rounded-md shadow-md">
            <AdminSoftwareTable
              softwareList={filteredSoftware}
              ordering={ordering}
              setOrdering={setOrdering}
              highlighQuery={search}
            />
          </div>

          <AdminSoftwarePagination
            className="mt-4 [&_a]:text-xs [&_a]:p-1 [&_a]:h-7"
            page={page}
            setPage={setPage}
            pagesCount={pagesCount}
          />
        </>
      ) : (
        <div>
          <Skeleton className="h-14 w-full my-1" />
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton className="h-10 w-full my-1" key={i} />
          ))}
        </div>
      )}
    </div>
  )
})
